import axios from '@/axios/api';
import { definitions } from '@/types/api';
import {
  reactive, computed, ComputedRef, toRefs, ref,
} from 'vue';
import { MessageApi } from 'ant-design-vue/lib/message';
import { message } from 'ant-design-vue';
import {
  CreateI, DeleteI, QueryI, UpdateI,
} from '@/interface/basicOperations';
import validate from '@/lib/validate';
import { BasicUser, DisableUser } from '@/interface/basicUser';
import userServices from '@/services/user.services';
import { promiseConfirm } from '@/lib/message';
import { TeacherColumnsI, Key } from './interface';

const { delTeacherByIds } = userServices();

/**
 * 服务器查询教师列表
 * @param reqData
 * @returns
 */
const queryUserList = async (reqData: definitions['QueryUserRequest']): Promise<definitions['BaseResponseBody«Page«User»»']> => {
  try {
    const response = await axios.queryUserList(reqData);
    const { data } = response;
    return data;
  } catch (err) {
    throw new Error(err);
  }
};
/**
 * 获取默认密码
 * @param
 * @returns
 */
const getDefaultPassword = async (): Promise<string> => {
  try {
    const { data } = await axios.getDefaultPassword();
    return data.data;
  } catch (err) {
    throw new Error(err);
  }
};
/**
 * 获取服务器获取后的教师列表
 * @param reqData
 * @returns
 */
const getUserList = async (reqData: definitions['QueryUserRequest']) => {
  try {
    const { data } = await queryUserList(reqData);
    const list = data?.list?.map((item) => ({
      name: item.name || '',
      code: item.code || '',
    })) || [];
    return {
      list,
      totalRecords: data?.totalRecords || 0,
    };
  } catch (err) {
    throw new Error(err);
  }
};

export interface TeacherI extends CreateI<BasicUser, BasicUser>, QueryI<unknown, void>, UpdateI<BasicUser, BasicUser>, DeleteI<DisableUser, BasicUser> {
  list: BasicUser[];
  totalRecords: number;
  /**
   * 重置密码
   */
  resetPassword: (reqData: {code: string}) => Promise<BasicUser>;
}

export class Teacher implements TeacherI {
  // eslint-disable-next-line class-methods-use-this
  async create(reqData: { code: string; name: string }): Promise<BasicUser> {
    try {
      const addUserReq = {
        isTeacher: 'true',
        ...reqData,
      };
      await axios.addUser(addUserReq);
      return reqData;
    } catch (err) {
      throw new Error(err);
    }
  }

  async query(reqData: { pageSize: number; pageNo: number }) {
    try {
      const queryParam = {
        disable: 'false',
        role: 1,
      };
      const { list, totalRecords } = await getUserList({ ...reqData, queryParam });
      this.list = list;
      this.totalRecords = totalRecords;
    } catch (err) {
      throw new Error(err);
    }
  }

  async update(reqData: { name: string; code: string }): Promise<BasicUser> {
    try {
      const response = await axios.changeUserSignature(reqData);
      const target = this.list.find((t) => t.code === reqData.code);
      if (target) { target.name = response.data.name; }
      return target as BasicUser;
    } catch (err) {
      throw new Error(err);
    }
  }

  async resetPassword(reqData: { code: string }): Promise<BasicUser> {
    try {
      await axios.adminResetPassword(reqData);
      return this.list.find((t) => t.code === reqData.code) as BasicUser;
    } catch (err) {
      throw new Error(err);
    }
  }

  async delete(reqData: { code: string; disable: boolean }): Promise<BasicUser> {
    try {
      await axios.changeUserSignature(reqData);
      return this.list.find((t) => t.code === reqData.code) as BasicUser;
    } catch (err) {
      throw new Error(err);
    }
  }

  /**
   * 获取默认密码
   * @param reqData
   * @returns
   */
  async getDefaultPasswordValue() {
    try {
      const data = await getDefaultPassword();
      this.defaultPassword = data;
    } catch (err) {
      throw new Error(err);
    }
  }

  list: BasicUser[] = [];

  totalRecords = 0;

  defaultPassword='123456';
}

export const editFormRules = {
  name: [
    { reg: /^[ A-Za-z0-9\u4E00-\u9FA5·。]+$/, message: '存在异常字符，请重新输入！' },
    { max: 20, min: 2, message: '请输入2-20个字符内的姓名！' },
  ],
  code: [
    { reg: /^[a-zA-Z0-9]+$/, message: '存在异常字符，请重新输入！' },
    { max: 20, min: 1, message: '请输入20个字符内的姓名或账号！' },
  ],
};

export default (Message: MessageApi = message) => {
  const teacherModule = reactive(new Teacher());

  const pagination = reactive({
    pageSize: 12,
    pageNo: 1,
  });

  const columns = [
    {
      title: '序号',
      dataIndex: 'index',
      key: 'index',
      width: 120,
    },
    {
      title: '教师名称',
      dataIndex: 'name',
      ellipsis: true,
      key: 'name',
    },
    {
      title: '账号',
      dataIndex: 'code',
      ellipsis: true,
      key: 'code',
    },
    {
      title: '操作',
      dataIndex: 'operation',
      align: 'center',
      slots: { customRender: 'operation' },
    },
  ];

  const selectState = reactive<{
    selectedRowKeys: Key[];
    loading: boolean;
  }>({
    selectedRowKeys: [],
    loading: false,
  });

  const hasSelected = computed(() => selectState.selectedRowKeys.length > 0);

  const modalVisible = reactive({
    reset: false,
    edit: false,
    plus: false,
  });

  const modalConfirmLoading = reactive({
    reset: false,
    edit: false,
    plus: false,
  });

  const editForm = reactive({
    code: '',
    name: '',
  });
  const defaultPassword: ComputedRef<string> = computed((): string => teacherModule.defaultPassword);

  const dataSource: ComputedRef<TeacherColumnsI[]> = computed((): TeacherColumnsI[] => teacherModule.list.map((t, index): TeacherColumnsI => ({
    key: t.code,
    index: (pagination.pageNo - 1) * pagination.pageSize + index + 1,
    name: t.name,
    code: t.code,
  })));

  const queryTeacher = async (pn: number = pagination.pageNo, ps: number = pagination.pageSize) => {
    try {
      const queryUserLisRequest = {
        pageNo: pn,
        pageSize: ps,
      };
      await teacherModule.query(queryUserLisRequest);
      return teacherModule.list;
    } catch (err) {
      throw new Error(err);
    }
  };

  const handlePageChange = async () => {
    try {
      await teacherModule.query(pagination);
    } catch (err) {
      throw new Error(err);
    }
  };

  const handleSelectedRowsChange = (selectedRowKeys: Key[]) => {
    selectState.selectedRowKeys = selectedRowKeys;
  };

  const handleDeleteClick = async () => {
    try {
      // Message.info('删除功能暂未开放');
      promiseConfirm('将删除教师下所有班级学生账号及所有实验数据，此操作不可逆，是否确认删除？', async () => {
        selectState.loading = true;
        await delTeacherByIds(selectState.selectedRowKeys as string[]);
        Message.success('删除成功!');
        await teacherModule.query(pagination);
        selectState.loading = false;
      }, undefined, () => {
        selectState.loading = false;
      });
    } catch (err) {
      throw new Error(err);
    }
  };

  const handleEditClick = (t: TeacherColumnsI) => {
    editForm.code = t.code;
    editForm.name = t.name;
    modalVisible.edit = true;
  };

  const handleResetPasswordClick = (t: TeacherColumnsI) => {
    editForm.code = t.code;
    editForm.name = t.name;
    modalVisible.reset = true;
  };

  const handlePlusClick = () => {
    modalVisible.plus = true;
  };

  const handleEditCancel = () => {
    modalVisible.edit = false;
    editForm.code = '';
    editForm.name = '';
  };

  const handleResetPasswordCancel = () => {
    modalVisible.reset = false;
  };

  const handlePlusCancel = () => {
    editForm.name = '';
    editForm.code = '';
    modalVisible.plus = false;
  };

  const handleEditOk = async () => {
    try {
      validate(editForm, editFormRules);
      modalConfirmLoading.edit = true;
      await teacherModule.update({
        name: editForm.name,
        code: editForm.code,
      });
      Message.success('修改成功');
      await teacherModule.query(pagination);
      handleEditCancel();
    } catch (err) {
      throw new Error(err);
    } finally {
      modalConfirmLoading.edit = false;
    }
  };

  const handleResetPasswordOk = async () => {
    try {
      modalConfirmLoading.reset = true;
      await teacherModule.resetPassword({ code: editForm.code });
      handleResetPasswordCancel();
    } catch (err) {
      throw new Error(err);
    } finally {
      modalConfirmLoading.reset = false;
    }
  };

  const handlePlusOk = async () => {
    try {
      validate(editForm, editFormRules);
      modalConfirmLoading.plus = true;
      await teacherModule.create(editForm);
      message.success('添加成功');
      await teacherModule.query(pagination);
      modalConfirmLoading.plus = false;
      handlePlusCancel();
    } catch (err) {
      throw new Error(err);
    } finally {
      modalConfirmLoading.plus = false;
    }
  };

  return {
    pagination,
    columns,
    dataSource,
    teacherModule,
    hasSelected,
    modalVisible,
    modalConfirmLoading,
    editForm,
    ...toRefs(selectState),
    defaultPassword,
    queryTeacher,
    handlePageChange,
    handleSelectedRowsChange,
    handleDeleteClick,
    handleEditClick,
    handleResetPasswordClick,
    handlePlusClick,
    handleEditCancel,
    handleResetPasswordCancel,
    handlePlusCancel,
    handleEditOk,
    handleResetPasswordOk,
    handlePlusOk,
  };
};
